import {
  EXPORT_FILE_SOURCE,
  EXPORT_FILE_STATUS,
  EXPORT_FILE_STATUSES,
  exportFileSourceSchema,
  exportFileStatusSchema,
  FTP_ACCOUNT,
  ftpAccountSchema,
  statusLabel,
  type ExportFileSource,
  type ExportFileStatus,
  type FtpAccount,
} from '@flux/shared';
import { ListFilter, Search } from 'lucide-react';
import type { JSX } from 'react';
import { useState } from 'react';
import { z } from 'zod';
import { Badge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu.js';
import { OriginFilter } from '@/components/filters/origin-filter.js';
import { Input } from '@/components/ui/input.js';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select.js';
import { useOriginFilter } from '@/lib/origin-filter.js';

const ALL_VALUE = '__all__';

export interface FilesFiltersValue {
  source?: ExportFileSource;
  ftpAccount?: FtpAccount;
  status: ExportFileStatus[];
  from?: string;
  to?: string;
  search?: string;
}

export type FilesFiltersPatch = Partial<{
  source: ExportFileSource | undefined;
  ftpAccount: FtpAccount | undefined;
  status: ExportFileStatus[] | undefined;
  from: string | undefined;
  to: string | undefined;
  search: string | undefined;
}>;

function readRecord(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};
}

/** Lit les filtres de l'écran Fichiers depuis `useSearch({strict:false})` (`02` §5). */
export function readFilesFilters(rawSearch: unknown): FilesFiltersValue {
  const search = readRecord(rawSearch);
  const source = exportFileSourceSchema.safeParse(search.source);
  const ftpAccount = ftpAccountSchema.safeParse(search.ftpAccount);
  const status = z.array(exportFileStatusSchema).safeParse(search.status);
  return {
    ...(source.success ? { source: source.data } : {}),
    ...(ftpAccount.success ? { ftpAccount: ftpAccount.data } : {}),
    status: status.success ? status.data : [],
    ...(typeof search.from === 'string' ? { from: search.from } : {}),
    ...(typeof search.to === 'string' ? { to: search.to } : {}),
    ...(typeof search.search === 'string' ? { search: search.search } : {}),
  };
}

export interface FilesFiltersProps {
  value: FilesFiltersValue;
  onChange: (patch: FilesFiltersPatch) => void;
}

/**
 * Barre de filtres — `02` §5 : origine, source, compte FTP, statut, période (+ recherche par nom).
 * Même gabarit que `ListToolbar` (recherche puis filtres de 36 px sur une ligne
 * qui passe à la ligne), mais la recherche ne part qu'à la validation du
 * formulaire (Entrée ou loupe).
 */
export function FilesFilters({ value, onChange }: FilesFiltersProps): JSX.Element {
  const [searchDraft, setSearchDraft] = useState(value.search ?? '');
  const { origin, setOrigin } = useOriginFilter();

  return (
    <div className="flex w-full flex-wrap items-center gap-2">
      <form
        className="relative w-full sm:w-72"
        onSubmit={(event) => {
          event.preventDefault();
          onChange({ search: searchDraft.trim() || undefined });
        }}
      >
        <Input
          type="search"
          placeholder="Nom du fichier…"
          aria-label="Rechercher un fichier"
          className="w-full pr-9 [&::-webkit-search-cancel-button]:hidden"
          value={searchDraft}
          onChange={(event) => {
            setSearchDraft(event.target.value);
          }}
        />
        <button
          type="submit"
          aria-label="Rechercher"
          className="absolute right-1.5 top-1/2 inline-flex size-6 -translate-y-1/2 items-center justify-center rounded-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Search className="size-4" aria-hidden="true" />
        </button>
      </form>

      <OriginFilter value={origin} onChange={setOrigin} includePennylane />

      <Select
        value={value.source ?? ALL_VALUE}
        onValueChange={(next) => {
          onChange({ source: next === ALL_VALUE ? undefined : exportFileSourceSchema.parse(next) });
        }}
      >
        <SelectTrigger className="w-44" aria-label="Source">
          <SelectValue placeholder="Toutes les sources" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL_VALUE}>Toutes les sources</SelectItem>
          {Object.values(EXPORT_FILE_SOURCE).map((entry) => (
            <SelectItem key={entry.code} value={entry.code}>
              {entry.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={value.ftpAccount ?? ALL_VALUE}
        onValueChange={(next) => {
          onChange({ ftpAccount: next === ALL_VALUE ? undefined : ftpAccountSchema.parse(next) });
        }}
      >
        <SelectTrigger className="w-52" aria-label="Compte FTP">
          <SelectValue placeholder="Tous les comptes" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL_VALUE}>Tous les comptes</SelectItem>
          {Object.values(FTP_ACCOUNT).map((entry) => (
            <SelectItem key={entry.code} value={entry.code}>
              {entry.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button type="button" variant="outline" aria-label="Filtrer par statut">
            <ListFilter aria-hidden="true" />
            Statut
            {value.status.length > 0 ? <Badge tone="gray">{value.status.length}</Badge> : null}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          {EXPORT_FILE_STATUSES.map((code) => (
            <DropdownMenuCheckboxItem
              key={code}
              checked={value.status.includes(code)}
              onCheckedChange={(checked) => {
                const next = checked
                  ? [...value.status, code]
                  : value.status.filter((entry) => entry !== code);
                onChange({ status: next.length > 0 ? next : undefined });
              }}
            >
              {statusLabel(EXPORT_FILE_STATUS, code)}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <div className="flex items-center gap-2">
        <Input
          type="date"
          aria-label="Généré depuis le"
          className="w-36"
          value={value.from ?? ''}
          onChange={(event) => {
            onChange({ from: event.target.value || undefined });
          }}
        />
        <span className="text-sm text-muted-foreground" aria-hidden="true">
          →
        </span>
        <Input
          type="date"
          aria-label="Généré jusqu'au"
          className="w-36"
          value={value.to ?? ''}
          onChange={(event) => {
            onChange({ to: event.target.value || undefined });
          }}
        />
      </div>
    </div>
  );
}
